<template>
  <f7-page class="main-newfieldpage">
    <template #fixed>
      <TheNavbar
        nav-title="Nueva Finca"
        back-btn-enable
        :style="appTheme"
      />
    </template>

    <div>
      <f7-button
        class="margin-top margin-bottom bg-blue-Contracts margin-horizontal"
        button
        @click="openSigpac"
      >
        {{ $t('common.map') }}
      </f7-button>
    </div>

    <div>
      <form
        ref="form"
        class="formulario"
        @submit.prevent="addNewField()"
      >
        <f7-list>
          <li class="padding-horizontal">
            <div class="item-title item-label padding-top">
              {{ $t('common.farmer') }}
            </div>
          </li>
          <f7-list-item
            :title="$t('common.farmer')"
            smart-select
            :disabled="isFarmerReadOnly"
            :smart-select-params="{
              closeOnSelect: true,
              setValueText: false,
              cssClass: 'new-field-smart-select',
            }"
            :after="selectableFarmers.find((item) => item.id === farmer)?.name"
          >
            <select
              v-model="farmer"
              name="farmer"
            >
              <option
                v-for="(f) in selectableFarmers"
                :key="f.id"
                :value="f.id"
              >
                {{ f.name }}
              </option>
            </select>
          </f7-list-item>

          <f7-list-input
            ref="codeField"
            class="margin-top"
            :label="$t('common.code')"
            type="text"
            :placeholder="$t('common.code')"
            info="(longitud máxima 10)"
            required
            validate
            pattern="[A-Za-z 0-9]{1,10}"
            clear-button
            :disabled="prefijoCodigoFinca !== null"
            :value="code"
            @input="code = $event.target.value"
          />

          <f7-list-input
            class="margin-top"
            :label="$t('common.name')"
            type="text"
            :placeholder="$t('common.name')"
            required
            validate
            clear-button
            :value="name"
            @input="name = $event.target.value"
          />

          <f7-list-input
            class="margin-top"
            :label="`Superficie (${surfaceUnit})`"
            type="number"
            placeholder="Superficie"
            required
            validate
            :min="(surfaceUnit === 'Ha') ? 0.0001 : 1"
            :step="(surfaceUnit === 'Ha') ? 0.0001 : 1"
            clear-button
            :value="surface"
            @input="surface = $event.target.value"
          />
          <li
            class="margin-top"
            :style="{
              'padding-left': '16px',
            }"
          >
            <div class="item-title item-label">
              Régimen de tenencia
            </div>
          </li>
          <f7-list-item
            title="Régimen de tenencia"
            smart-select
            :smart-select-params="{
              closeOnSelect: true,
              setValueText: false,
              cssClass: 'new-field-smart-select',
            }"
            :after="ownershipTypes.find((item) => item.id === selectedOwnershipType)?.name"
          >
            <select
              v-model="selectedOwnershipType"
              name="ownershiptype"
              required
              validate
            >
              <option value="" />
              <option
                v-for="(f) in ownershipTypes"
                :key="f.id"
                :value="f.id"
              >
                {{ f.name }}
              </option>
            </select>
          </f7-list-item>
          <f7-list-input
            class="margin-top padding-bottom zona-select"
            label="Zona"
            type="select"
            placeholder="Zona"
            validate
            clear-button
            :value="zoneSelected"
            @input="zoneSelected = $event.target.value"
          >
            <option
              value="0"
              selected
              disabled
            />
            <option
              v-for="(f) in zones"
              :key="f.id"
              :value="f.id"
            >
              {{ f.name }}
            </option>
          </f7-list-input>
        </f7-list>

        <ComplementaryData
          class="margin-top complementary-data"
          :entity="1037"
          :dcs-data="dcData"
          @data-changed="complementaryDataChanged"
        />
        <div class="btn-fixed-bottom">
          <f7-button
            type="submit"
            @click="clickForm"
          >
            {{ $t('common.save') }}
          </f7-button>
        </div>
      </form>
    </div>
    <MapPopUp
      :popup-opened="sigpacOpen"
      :locations="(geoJSON) ? [geoJSON] : []"
      @popup-close="sigpacOpen = false;"
      @geometry-saved="geometrySaved"
    />
  </f7-page>
</template>

<script>
import { nextTick } from 'vue';
import { useParamRef } from '@erpagroapp/params';

import FarmerService from '@/js/services/contracts/Farmers';
import FieldService from '@/js/services/contracts/Fields';
import ComplementaryData from '@/components/contracts/ComplementaryData.vue';
import MapPopUp from '@/components/contracts/MapPopUp.vue';
import TheNavbar from '@/components/TheNavbar.vue';
import MasterDataService from '@/js/services/contracts/MasterData';
import { LogError } from '@/js/common/HIDebuggingSupport';
import { ConfirmDialogGH, HiNotification } from '@/js/common/HINotificationSupport';
import { f7 } from 'framework7-vue';

export default {
  name: 'NewFieldPage',
  components: {
    ComplementaryData,
    MapPopUp,
    TheNavbar,
  },
  props: {
    farmerId: {
      type: Number,
      default: 0,
    },
    fieldId: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isFarmerReadOnly: false,
      farmer: 0,
      selectableFarmers: [],
      ownershipTypes: [],
      name: '',
      code: '',
      surface: 0,
      ownershipType: 0,
      currentField: {},
      dcData: {},
      sigpacOpen: false,
      geoJSON: {},
      surfaceUnit: localStorage.getItem('unidadSuperficie'),
      zones: [],
      zoneSelected: 0,
      selectedOwnershipType: undefined,
      prefijoCodigoFinca: useParamRef('PrefijoCodigoFinca'),
    };
  },

  computed: {
    appTheme() {
      return {
        '--f7-navbar-bg-color': 'var(--his-dark-blue)',
        '--f7-navbar-text-color': '#fff',
      };
    },

    newField() {
      return {
        code: this.code,
        name: this.name,
        surface: (this.surfaceUnit === 'Ha') ? this.surface * 10000 : this.surface,
        farmer: this.farmer,
        ownershipType: this.ownershipType,
        dcs: this.dcData,
        geoJSON: this.geoJSON,
        Zone: this.zoneSelected,
      };
    },
  },

  async mounted() {
    f7.preloader.show();
    await this.loadZones();

    if (this.fieldId > 0) {
      await this.loadField();
    }

    await this.loadFarmers();
    await this.loadOwnershipTypes();
    nextTick(() => {
      if (this.currentField.ownershipType > 0) {
        this.selectedOwnershipType = this.currentField.ownershipType;
      } else if (this.ownershipType > 0) {
        this.selectedOwnershipType = this.ownershipType;
      }
    });
    f7.preloader.hide();
  },
  methods: {
    async loadFarmers() {
      let farmers;
      if (this.farmerId > 0) {
        farmers = [await FarmerService.getById(this.farmerId)];
        this.farmer = farmers[0].id;
        this.isFarmerReadOnly = true;
      } else {
        farmers = await FarmerService.getAll();
        this.isFarmerReadOnly = false;
      }
      this.selectableFarmers = farmers;
    },

    async loadField() {
      const field = await FieldService.getById(this.fieldId);
      this.name = field.name;
      this.code = field.code;
      this.surface = (this.surfaceUnit === 'Ha') ? field.surface / 10000 : field.surface;
      this.ownershipType = field.ownershipType;
      this.geoJSON = field.geoJSON;
      this.currentField = field;
      this.dcData = field.dcs;
      this.zoneSelected = field.Zone;
    },

    async addNewField() {
      const uniqueCode = await this.validateUniqueCode(this.code);
      if (!uniqueCode) {
        return;
      }
      try {
        const auxField = {
          ...this.newField,
        };
        auxField.sync_status = 1;

        if (this.fieldId > 0) {
          await FieldService.updateById(this.fieldId, auxField);
        } else {
          await FieldService.add(auxField);
        }

        f7.views.main.router.back('');
      } catch (e) {
        LogError(this, this.addNewField.name, e);
      }
    },
    async loadZones() {
      const zones = await MasterDataService.getAllZones();
      this.zones = zones;
    },
    async loadOwnershipTypes() {
      this.ownershipTypes = await MasterDataService.getAllOwnershipTypes();
      if (this.fieldId > 0) {
        this.ownershipType = this.currentField.ownershipType;
      } else {
        this.ownershipType = this.ownershipTypes[0].id;
        if (this.selectedOwnershipType !== undefined) {
          this.selectedOwnershipType = this.ownershipType;
        }
      }
    },
    complementaryDataChanged(newValue) {
      this.dcData = newValue;
    },
    async clickForm() {
      const {
        form,
      } = this.$refs;
      if (!form.checkValidity()) {
        if (this.ownershipType > 0) {
          HiNotification.Alert('', 'Faltan campos obligatorios');
        } else {
          HiNotification.Alert('El régimen de tenencia es obligatorio', 'Faltan campos obligatorios');
        }
      }
    },
    async validateUniqueCode(pCode) {
      if (this.fieldId === 0) {
        const tempField = await FieldService.getByCode(pCode);
        if (tempField && tempField.code === '') {
          return true;
        }

        if (tempField) {
          HiNotification.Alert(`El Código de finca ${this.code} ya existe`, 'Código no único');
          this.$refs.codeField.errorMessage = 'Código finca ya existe';
          this.$refs.codeField.errorMessageForce = true;
          return false;
        }
      }
      return true;
    },
    openSigpac() {
      this.sigpacOpen &&= false;
      this.sigpacOpen = true;
    },
    async geometrySaved(value) {
      let selectedSurface = Number.parseInt(value.features[0].properties.dn_surface, 10);
      selectedSurface = (this.surfaceUnit === 'Ha') ? selectedSurface / 10000 : selectedSurface;

      if (selectedSurface !== this.surface) {
        const res = await ConfirmDialogGH(`Quieres modificar la superficie de la finca por la seleccionada? <strong>${selectedSurface} ${this.surfaceUnit}</strong>`, '');

        if (res) {
          this.surface = selectedSurface;
        }
      }

      const newGeo = value.features[0].geometry.coordinates[0];
      value.features[0].geometry.coordinates = newGeo;

      this.geoJSON = value;
    },
  },
};
</script>

<style lang="scss">
.zona-select {
  .item-content {
    .item-inner {
      .item-input-wrap {
        select {
          height: 45px;
        }
      }
    }
  }
}

.new-field-smart-select {
  --f7-navbar-bg-color: var(--his-dark-blue);
}

.main-newfieldpage {
  --available-viewport-height: calc(100vh - var(--information-bar-height));
  --information-bar-padding-bottom: 0;

  .btn-fixed-bottom {
    bottom: var(--information-bar-height);
  }

  .formulario {
    height: calc(90vh - (var(--available-viewport-height) - 90vh));
    overflow-y: scroll;
  }

  .accordion-item .item-link {
    background-color: rgb(85 96 128 / 10%);
  }

  .page-content {
    background: var(--his-whispering-lilac);
  }

  .complementary-data {
    margin-bottom: 100px;
  }

  input[type="text"],
  input[type="password"],
  input[type="search"],
  input[type="email"],
  input[type="tel"],
  input[type="url"],
  input[type="date"],
  input[type="month"],
  input[type="datetime-local"],
  input[type="time"],
  input[type="number"],
  select,
  .smart-select .item-content .item-inner,
  textarea {
    border: 1px solid var(--his-silver);
    border-radius: 6px;
    -webkit-border-radius: 6px;
    -moz-border-radius: 6px;
    -ms-border-radius: 6px;
    -o-border-radius: 6px;
  }

  .item-content {
    .item-inner {
      .item-input-wrap {
        input {
          height: 50px;
          padding-left: 10px;
        }

        .input-clear-button {
          margin-top: -4px;
          margin-right: 15px;
        }
      }
    }
  }
}

.hide-option {
  display: none !important;
}
</style>
